import { createContext, ReactNode, useState } from 'react'

import { TabValue } from '~/app/pages/SurgeonScheduler/TablesLayout/Practitioners/Table/PractitionerRow/PractitionerPopover'
import { Practitioner } from '~/storm'

export type DialogType = 'practitioner' | 'recurringAssignments'

type BaseState = {
    activeDialog: null
    dialogData: null
}

const initialState: BaseState = {
    activeDialog: null,
    dialogData: null,
}

type PractitionerState = {
    activeDialog: 'practitioner'
    dialogData: {
        practitioner: Practitioner
        defaultTab?: TabValue
    }
}

type DialogStates =
    | BaseState
    | PractitionerState
    | {
          activeDialog: 'recurringAssignments'
          dialogData: undefined
      }

type Actions = {
    setDialog(dialogState: DialogStates): void
    /** 
    If dialogType is provided, only close if the active dialog matches the provided type.
    Otherwise, close the active dialog. 
    */
    closeDialog(dialogType?: DialogType): void
}

export type DialogState<T extends DialogType> = Extract<DialogStates, { activeDialog: T }>
export type DialogData<T extends DialogType> = DialogState<T>['dialogData']

export const DialogRouterContext = createContext<DialogStates & Actions>({
    ...initialState,
    setDialog: () => {},
    closeDialog: () => {},
})

export const DialogRouterProvider = ({ children }: { children: ReactNode }) => {
    const [dialogState, setDialogState] = useState<DialogStates>(initialState)

    const closeDialog = (dialogType?: DialogType) =>
        setDialogState(prev => {
            if (typeof dialogType === 'string' && prev.activeDialog !== dialogType) {
                return prev
            }
            return { ...prev, activeDialog: null, dialogData: null }
        })

    return <DialogRouterContext value={{ ...dialogState, setDialog: setDialogState, closeDialog }}>{children}</DialogRouterContext>
}
